import { LinearProgress } from "@evergis/ui";
import React, { FC, ReactElement, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import { ResourceTypeLink } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { Card } from "../Card/Card";
import { NoContent } from "./NoContent";
import { NoProjects } from "./NoProjects";
import { NoLayers } from "./NoLayers";
import { NoTables } from "./NoTables";
import { CardGroupContainer, CardGroupContent } from "./styled";

import { isFilterEmpty, useFetchParams } from "../../hooks/useFetchParams";
import { useResourceType } from "../../../../hooks/useResourceType";
import { useResourceEmpty } from "../../hooks/useResourceEmpty";
import { getProjectName } from "../../../../ducks/project/selectors";

import { ResourcesListProps } from "../../types";

export const CardList: FC<ResourcesListProps> = ({ resources, currentPage, onPageChange, totalCount }) => {
  const { t } = useTranslation("catalog");
  const [resourceType] = useResourceType();
  const fetchParams = useFetchParams();
  const userHasNoData = totalCount === 0;
  const userHasNoResource = userHasNoData && isFilterEmpty(fetchParams);
  const currentProject = useSelector(getProjectName);

  const resolveNoResource = useCallback((): ReactElement => {
    switch (resourceType) {
      case ResourceTypeLink.Project:
        return <NoProjects headerText={t("createMapMessage")} />;
      case ResourceTypeLink.Layer:
        return <NoLayers headerText={t("createLayerMessage")} />;
      case ResourceTypeLink.Table:
        return <NoTables headerText={t("createTableMessage")} />;
      default:
        return null;
    }
  }, [resourceType, t]);

  useResourceEmpty(userHasNoResource);

  if (userHasNoResource) {
    return resolveNoResource();
  }

  if (userHasNoData) {
    return <NoContent />;
  }

  return (
    <InfiniteScroll
      dataLength={resources?.length}
      next={() => onPageChange(currentPage + 1)}
      hasMore={resources?.length < (totalCount as number)}
      loader={<LinearProgress />}
      scrollableTarget="cardListContainer"
    >
      <CardGroupContainer dialog={!!currentProject}>
        {resources.map((resource, index) => (
          <CardGroupContent key={index}>
            <Card resource={resource} />
          </CardGroupContent>
        ))}
      </CardGroupContainer>
    </InfiniteScroll>
  );
};
